import React, { FC, useCallback, useContext } from "react";
import classNames from "classnames/dedupe";
import { SearchContext } from "../SearchContext";
import { ISearchResultsPage } from "./SearchResults.interface";
import Divider from "../../../DataDisplay/Divider/Divider";
import SearchResultsWithDetails from "./SearchResultsWithDetails";
import Row from "../../../Fundamentals/Grid/Row";
import { Typography } from "../../../Fundamentals/Typography/Typography";
import SearchResultsItem from "./SearchResultsItem";

const SearchResultsPage: FC<ISearchResultsPage> = ({
  searchResults,
  withDetails = false,
  minSearchTermLength,
  isGenderDriven = false,
  closeOnSelection = true,
  nonFixed = true,
  classes,
}) => {
  const {
    recent,
    popular,
    highLight,
    results,
    category,
    detailResults,
    relevantResults,
  } = searchResults;

  const { searchTerm, closeSearch, handleListItemClick } =
    useContext(SearchContext);

  const handleResultClick = useCallback(
    (item) => {
      handleListItemClick(item.id);
      closeOnSelection && closeSearch();
    },
    [closeOnSelection, closeSearch, handleListItemClick],
  );

  const renderWithDetailsResults = useCallback(
    () => (
      <>
        <h3 className="text-color-white">Top Suggestions</h3>
        <div className="search-destination">
          <p>
            Search in{" "}
            <span className="font-weight-bold text-highlighted">
              {category}
            </span>{" "}
            category
          </p>
        </div>

        <Divider color="white" classes={{ root: "mt-6 mb-7" }} />

        <Row>
          {detailResults?.map((item) => (
            <SearchResultsWithDetails
              key={item.id}
              item={item}
              onSearchResultClick={handleResultClick}
            />
          ))}
        </Row>

        {relevantResults && (
          <>
            <Divider color="white" classes={{ root: "mt-6 mb-7" }} />
            <Typography variant="text-desc" classes={{ root: "mb-7" }}>
              Relevant Articles
            </Typography>
            <Row>
              {relevantResults.map((item) => (
                <SearchResultsWithDetails
                  key={item.id}
                  item={item}
                  onSearchResultClick={handleResultClick}
                />
              ))}
            </Row>
          </>
        )}
      </>
    ),
    [category, detailResults, handleResultClick, relevantResults],
  );

  const renderDefaultResults = useCallback(
    () => (
      <>
        <div className="search-request-destination">
          <SearchResultsItem
            items={highLight}
            onSearchResultClick={handleResultClick}
          />
          <div className="search-destination">
            <p>
              Search in{" "}
              <span className="font-weight-bold text-highlighted">
                {category}
              </span>{" "}
              category
            </p>
          </div>
        </div>
        <Divider color="white" classes={{ root: "mt-6 mb-7" }} />
        <SearchResultsItem
          items={results}
          searchTerm={searchTerm}
          onSearchResultClick={handleResultClick}
        />
      </>
    ),
    [category, handleResultClick, highLight, results, searchTerm],
  );

  const renderRecentSearches = useCallback(
    () => (
      <>
        <h3 className="text-color-white">Recent Searches</h3>

        <SearchResultsItem
          items={recent}
          onSearchResultClick={handleResultClick}
        />

        <Divider color="white" classes={{ root: "mt-6 mb-7" }} />

        <h3 className="text-color-white">Popular Search Terms</h3>
        <SearchResultsItem
          items={popular}
          onSearchResultClick={handleResultClick}
        />
      </>
    ),
    [handleResultClick, popular, recent],
  );

  const renderContent = useCallback(() => {
    if (withDetails && searchTerm.length >= minSearchTermLength)
      return renderWithDetailsResults();
    if (searchTerm.length >= minSearchTermLength) {
      return renderDefaultResults();
    }
    return renderRecentSearches();
  }, [
    minSearchTermLength,
    renderDefaultResults,
    renderRecentSearches,
    renderWithDetailsResults,
    searchTerm.length,
    withDetails,
  ]);

  return (
    <div
      className={classNames("search-results-container", classes?.root, {
        "search-results--gender-driven": isGenderDriven,
        "search-results-container--detailed": withDetails,
        "position-relative": nonFixed,
        "top-0": nonFixed,
      })}
    >
      <div className="search-results-container--content text-color-white">
        {renderContent()}
      </div>
    </div>
  );
};

export default SearchResultsPage;
